import { integrationApi } from "@repo/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { getCronExpression } from "../../utils/schedule";

interface UseUpdateCopilotStudioIntegrationProps {
  integrationId: number;
  onSuccess: (
    message: string,
    isDraft?: boolean,
    integrationType?: string,
  ) => void;
  onError: (message: string) => void;
}

export function useUpdateCopilotStudioIntegration({
  integrationId,
  onSuccess,
  onError,
}: UseUpdateCopilotStudioIntegrationProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      isEnabled,
      data,
    }: {
      isEnabled: boolean;
      data: Record<string, unknown>;
    }) => {
      const payload = {
        integrationId,
        displayName: (data.name as string) || "",
        isEnabled,
        credentials: {
          type: "OAUTH2",
          data: {
            clientTenantId: (data.tenantId as string) || "",
            clientId: (data.clientId as string) || "",
            ...(typeof data.clientSecret === "string" &&
            (data.clientSecret as string).trim().length > 0
              ? { clientSecret: (data.clientSecret as string).trim() }
              : {}),
            dataverseUrl: (data.dataverseUrl as string) || "",
          },
          metadata: {},
        },
        metadata: {
          accountId: (data.dataverseUrl as string) || "",
          description: (data.description as string) ?? "",
        },
        features: ((data.featureNames as string[]) || []).map(
          (featureName) => ({
            featureName,
            schedules: [
              {
                scheduleName: "copilotstudio-schedule",
                cronExpression: getCronExpression(
                  (data.frequency as string) || "24hours",
                  data.importTime as Date | null,
                ),
              },
            ],
          }),
        ),
      };
      return integrationApi.updateIntegration(integrationId, payload);
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: "M54/cu",
          defaultMessage: "Failed to update integration",
          description: "Error message for integration update failure",
        }),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      queryClient.invalidateQueries({
        queryKey: ["integration", integrationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationCredentials", integrationId],
      });
      const isDraft = !variables.isEnabled;
      if (isDraft) {
        onSuccess(
          intl.formatMessage(
            {
              id: "c2bz4O",
              defaultMessage:
                "You can resume the setup of {integrationType} at any time.",
              description: "Draft save success message",
            },
            { integrationType: variables.data.name as string },
          ),
          true,
          variables.data.name as string,
        );
      } else {
        onSuccess(
          intl.formatMessage({
            id: "1rvQ3c",
            defaultMessage: "Integration updated successfully",
            description: "Success message for integration update",
          }),
          false,
        );
      }
    },
  });
}
